import _ from 'lodash';
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, publishLast, catchError, refCount, finalize } from 'rxjs/operators';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';
import { ProgressService } from 'src/app/shared/services/progress.service';
import { InjectorModule } from 'src/app/shared/modules/injector.module';
import { HttpGetOptions } from 'src/app/shared/types/http-get-options.type';
import { Router } from '@angular/router';
import { AppConfigService } from './app-config.service';
import { ApiVersions } from './api-version';
import { MatDialog } from '@angular/material/dialog';
import {
  TenantPlanCheckResult,
  TenantPlanErrorModalComponent
} from '../components/tenant-plan-error-modal/tenant-plan-error-modal.component';
import { AuthService } from 'src/app/auth/auth.service';
import { ErrorHandler, ErrorHandlerFactory } from './error-handler-factory.service';

/** a function that returns a function. Handle checking plan errors response */
export const handlePlanError =
  (dialog: MatDialog) =>
  (errorResponse: HttpErrorResponse): boolean => {
    const body = errorResponse.error;

    const planError: TenantPlanCheckResult | null =
      body && typeof body === 'object' && 'canTakeAction' in body && !body.canTakeAction
        ? body
        : null;

    if (!planError) {
      return false;
    }
    TenantPlanErrorModalComponent.openDialog(
      dialog,
      planError.reason,
      planError.callToAction,
      planError.displayTitleIcon
    );
    return true;
  };

@Injectable({
  providedIn: 'root'
})
export abstract class BaseService {
  private snackbarService: SnackBarService;
  private progressService: ProgressService;
  protected appConfigService: AppConfigService;
  protected authService: AuthService;
  private http: HttpClient;
  protected dialog: MatDialog;
  protected router: Router;
  protected handleError: ErrorHandler;

  constructor() {
    this.snackbarService = InjectorModule.injector.get(SnackBarService);
    this.progressService = InjectorModule.injector.get(ProgressService);
    this.appConfigService = InjectorModule.injector.get(AppConfigService);
    this.authService = InjectorModule.injector.get(AuthService);
    this.http = InjectorModule.injector.get(HttpClient);
    this.dialog = InjectorModule.injector.get(MatDialog);
    this.router = InjectorModule.injector.get(Router);
    const errorHandlerFactory = InjectorModule.injector.get(ErrorHandlerFactory);
    this.handleError = errorHandlerFactory.buildHandler(
      this.handle401,
      handlePlanError(this.dialog),
      this.progressService.finishLoading
    );
  }

  protected getBaseApiUrl(apiVersion: ApiVersions = ApiVersions.v0_1): string {
    let url = this.appConfigService.baseApiUrl;
    if (apiVersion != ApiVersions.unversioned) {
      url = url.concat(`${apiVersion}/`);
    }
    return url;
  }

  public get<TResult = any>(
    path: string,
    opts?: HttpGetOptions,
    apiVersion?: ApiVersions
  ): Observable<TResult>;

  public get<TResult = any>(
    path: string,
    ok404?: boolean,
    apiVersion?: ApiVersions,
    rethrowError?: boolean
  ): Observable<TResult>;

  public get<TResult = any>(
    path: string,
    optsOrFlag?: HttpGetOptions | boolean,
    apiVersion?: ApiVersions,
    rethrowError: boolean = false
  ): Observable<TResult> {
    let url = this.getBaseApiUrl(apiVersion).concat(path);

    const opts = typeof optsOrFlag === 'object' ? optsOrFlag : undefined;
    const ok404 = typeof optsOrFlag === 'boolean' ? optsOrFlag : false;
    return this.request('GET', url, null, opts, ok404, rethrowError);
  }

  getFile(path: string, apiVersion?: ApiVersions, cloudinary?: boolean): Observable<Blob> {
    let url;
    if (cloudinary) {
      url = `${this.appConfigService.config.cloudinary.publicImageUrl}${path}`;
    } else {
      url = this.getBaseApiUrl(apiVersion).concat(path);
    }
    return this.http.get<Blob>(url, { observe: 'response', responseType: 'blob' as 'json' }).pipe(
      map((res) => {
        if (res) {
          return res.body;
        }
      }),
      catchError((err: HttpErrorResponse, caught) => {
        if (err.status == 404) {
          this.openSnackBar('File not found');
          return throwError(err);
        } else {
          return this.handleError(err, true, false) as Observable<any>;
        }
      })
    );
  }

  public patch(
    path: string,
    body: any = undefined,
    apiVersion?: ApiVersions,
    rethrowError: boolean = false
  ) {
    let url = this.getBaseApiUrl(apiVersion).concat(path);
    let options = this.defaultRequestOptions();

    return this.request('PATCH', url, body, options, true, rethrowError);
  }

  public post(
    path: string,
    body: any,
    options?: object,
    apiVersion?: ApiVersions,
    rethrowError = false
  ) {
    let url = this.getBaseApiUrl(apiVersion).concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('POST', url, body, options, true, rethrowError);
  }

  public put(
    path: string,
    body?: any,
    options?: object,
    apiVersion?: ApiVersions,
    rethrowError: boolean = false
  ) {
    let url = this.getBaseApiUrl(apiVersion).concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('PUT', url, body, options, true, rethrowError);
  }

  public delete(
    path: string,
    options?: object,
    apiVersion?: ApiVersions,
    rethrowError: boolean = false
  ): Observable<any> {
    let url = this.getBaseApiUrl(apiVersion).concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('DELETE', url, null, options, true, rethrowError);
  }

  private request(
    method: string,
    url: string,
    body: any = undefined,
    options: any = undefined,
    ok404: boolean = false,
    rethrowError: boolean = false
  ): Observable<any> {
    this.progressService.startLoading(url);

    let params = this.setParams(options);
    let headers = this.setHeaders(options);
    let request = new HttpRequest(method, url, body, { headers: headers, params: params });
    return this.http
      .request(request)
      .pipe(map(this.extractData))
      .pipe(catchError((err, caught) => this.handleError(err, ok404, rethrowError)))
      .pipe(publishLast())
      .pipe(refCount())
      .pipe(finalize(() => this.progressService.stopLoading(url)));
  }

  private setParams(options: any): HttpParams {
    let params = new HttpParams();

    if (options && options.params) {
      _.each(options.params, function (value, key) {
        params = params.append(key, value);
      });
    }

    return params;
  }

  private setHeaders(options: any): HttpHeaders {
    return options && options.headers ? options.headers : new HttpHeaders();
  }

  public extractData(res) {
    if (_.isNil(res) || !res.ok) {
      return {};
    }

    return res.body;
  }

  private handle401 = (): boolean => {
    if (this.authService.isAuthenticated) {
      return false;
    }
    this.authService.setRedirectUri();
    this.router.navigate(['/session-expired'], {
      queryParams: { connection: sessionStorage.getItem('loggedInConnection') },
      skipLocationChange: true
    });
    return true;
  };

  public defaultRequestOptions() {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return httpOptions;
  }

  public openSnackBar(message: string, success: boolean = false) {
    this.snackbarService.open(message, success);
  }

  public goToRoute(route: string) {
    this.router.navigateByUrl(route);
  }
}
